/**
 * Per-user Product Analytics preferences (last-viewed SKU + pinned shortcuts).
 * Persisted in localStorage — no server API.
 */

export const MAX_PINS = 5;
export const DEFAULT_PRODUCT_SKU = 'AMBR';
export const STORAGE_PREFIX = 'bci.productPreferences.v1:';
/** Same-tab signal so Sidebar + Dashboard share pin/lastSku updates immediately. */
export const PREFERENCES_CHANGED_EVENT = 'bci:product-preferences-changed';

export interface PinnedProduct {
  sku: string;
  name: string;
}

export interface ProductPreferenceDocument {
  version: 1;
  lastSku: string | null;
  pins: PinnedProduct[];
  updatedAt: string;
}

export type PinResult =
  | { ok: true; document: ProductPreferenceDocument }
  | { ok: false; reason: 'limit' | 'duplicate' | 'invalid' | 'no_user' };

export interface ResolveInitialSkuOptions {
  catalogSkus: string[];
  forcedSku?: string | null;
  querySku?: string | null;
  lastSku?: string | null;
}

function emptyDocument(): ProductPreferenceDocument {
  return {
    version: 1,
    lastSku: null,
    pins: [],
    updatedAt: new Date().toISOString(),
  };
}

function normalizeUserKey(userKey: string): string {
  return userKey.trim().toLowerCase();
}

function normalizeSku(sku: string): string {
  return sku.trim().toUpperCase();
}

/**
 * Builds the localStorage key for a signed-in user.
 */
export function preferenceStorageKey(userKey: string): string {
  return `${STORAGE_PREFIX}${normalizeUserKey(userKey)}`;
}

function isPinnedProduct(value: unknown): value is PinnedProduct {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record.sku === 'string' && typeof record.name === 'string';
}

function parseDocument(raw: string | null): ProductPreferenceDocument {
  if (!raw) {
    return emptyDocument();
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return emptyDocument();
    }
    const record = parsed as Record<string, unknown>;
    const pinsRaw = Array.isArray(record.pins) ? record.pins : [];
    const pins = pinsRaw.filter(isPinnedProduct).map((p) => ({
      sku: normalizeSku(p.sku),
      name: p.name,
    }));
    const lastSku =
      typeof record.lastSku === 'string' && record.lastSku.trim()
        ? normalizeSku(record.lastSku)
        : null;
    return {
      version: 1,
      lastSku,
      pins,
      updatedAt:
        typeof record.updatedAt === 'string' ? record.updatedAt : new Date().toISOString(),
    };
  } catch {
    return emptyDocument();
  }
}

/**
 * Reads preferences for a user, or an empty document when missing/invalid.
 */
export function readPreferences(userKey: string | null): ProductPreferenceDocument {
  if (!userKey || !userKey.trim()) {
    return emptyDocument();
  }
  if (typeof window === 'undefined' || !window.localStorage) {
    return emptyDocument();
  }
  return parseDocument(window.localStorage.getItem(preferenceStorageKey(userKey)));
}

/**
 * Notifies other hook instances in this tab that preferences changed.
 */
export function notifyPreferencesChanged(): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(PREFERENCES_CHANGED_EVENT));
}

/**
 * Sidebar / tooltip label: `SKU (Name)`.
 */
export function formatPinnedProductLabel(product: PinnedProduct): string {
  const name = product.name?.trim();
  if (!name || name.toUpperCase() === product.sku.toUpperCase()) {
    return product.sku;
  }
  return `${product.sku} (${name})`;
}

/**
 * Hover tooltip with full SKU and name.
 */
export function formatPinnedProductTooltip(product: PinnedProduct): string {
  const name = product.name?.trim();
  if (!name || name.toUpperCase() === product.sku.toUpperCase()) {
    return product.sku;
  }
  return `${product.sku} — ${name}`;
}

/**
 * Persists a preference document for a user (no-op without userKey).
 */
export function writePreferences(
  userKey: string | null,
  doc: ProductPreferenceDocument
): void {
  if (!userKey || !userKey.trim()) {
    return;
  }
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  const toStore: ProductPreferenceDocument = {
    version: 1,
    lastSku: doc.lastSku ? normalizeSku(doc.lastSku) : null,
    pins: doc.pins.slice(0, MAX_PINS).map((p) => ({
      sku: normalizeSku(p.sku),
      name: p.name,
    })),
    updatedAt: new Date().toISOString(),
  };
  window.localStorage.setItem(preferenceStorageKey(userKey), JSON.stringify(toStore));
  notifyPreferencesChanged();
}

/**
 * Updates last-viewed SKU for the user.
 */
export function setLastSku(userKey: string | null, sku: string): ProductPreferenceDocument {
  const current = readPreferences(userKey);
  const next: ProductPreferenceDocument = {
    ...current,
    lastSku: normalizeSku(sku),
  };
  writePreferences(userKey, next);
  return readPreferences(userKey);
}

/**
 * Pins a product (max MAX_PINS). Does not auto-evict.
 */
export function pinProduct(userKey: string | null, product: PinnedProduct): PinResult {
  if (!userKey || !userKey.trim()) {
    return { ok: false, reason: 'no_user' };
  }
  if (!product.sku.trim()) {
    return { ok: false, reason: 'invalid' };
  }
  const sku = normalizeSku(product.sku);
  const current = readPreferences(userKey);
  if (current.pins.some((p) => p.sku === sku)) {
    return { ok: false, reason: 'duplicate' };
  }
  if (current.pins.length >= MAX_PINS) {
    return { ok: false, reason: 'limit' };
  }
  const next: ProductPreferenceDocument = {
    ...current,
    pins: [...current.pins, { sku, name: product.name || sku }],
  };
  writePreferences(userKey, next);
  return { ok: true, document: readPreferences(userKey) };
}

/**
 * Removes a pinned product by SKU (case-insensitive).
 */
export function unpinProduct(userKey: string | null, sku: string): ProductPreferenceDocument {
  const current = readPreferences(userKey);
  const target = normalizeSku(sku);
  const next: ProductPreferenceDocument = {
    ...current,
    pins: current.pins.filter((p) => p.sku !== target),
  };
  writePreferences(userKey, next);
  return readPreferences(userKey);
}

/**
 * Drops pins / lastSku that are no longer in the catalog.
 */
export function prunePreferences(
  userKey: string | null,
  validSkus: Set<string>
): ProductPreferenceDocument {
  const normalizedValid = new Set(
    [...validSkus].map((s) => normalizeSku(s)).filter(Boolean)
  );
  const current = readPreferences(userKey);
  const pins = current.pins.filter((p) => normalizedValid.has(p.sku));
  let lastSku = current.lastSku;
  if (lastSku && !normalizedValid.has(lastSku)) {
    lastSku = normalizedValid.has(DEFAULT_PRODUCT_SKU) ? DEFAULT_PRODUCT_SKU : null;
  }
  const next: ProductPreferenceDocument = {
    ...current,
    lastSku,
    pins,
  };
  writePreferences(userKey, next);
  return readPreferences(userKey);
}

/**
 * Resolves which SKU to show on Product Analytics load.
 * Priority: forcedSku → querySku → lastSku → AMBR → first catalog → AMBR fallback.
 */
export function resolveInitialSku(opts: ResolveInitialSkuOptions): string {
  const catalog = opts.catalogSkus.map((s) => normalizeSku(s)).filter(Boolean);
  const catalogSet = new Set(catalog);

  const pick = (sku: string | null | undefined): string | null => {
    if (!sku || !sku.trim()) {
      return null;
    }
    const normalized = normalizeSku(sku);
    return catalogSet.has(normalized) ? normalized : null;
  };

  return (
    pick(opts.forcedSku) ??
    pick(opts.querySku) ??
    pick(opts.lastSku) ??
    pick(DEFAULT_PRODUCT_SKU) ??
    catalog[0] ??
    DEFAULT_PRODUCT_SKU
  );
}
